import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import {
  ArrowLeft, Building, TrendingUp, TrendingDown, Minus,
  Phone, Users, AlertTriangle, CheckCircle, XCircle,
  Sparkles, Loader2, Clock, Target, Shield, ChevronRight,
  DollarSign, Pencil, RefreshCw, UserCircle, ListTodo, Plus, Circle, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MarkdownBlock({ content }) {
  if (!content) return null;
  const lines = content.split('\n');
  const els = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('## ')) {
      els.push(<h2 key={`k1-${i}`} className="text-base font-semibold text-foreground mt-4 mb-1.5">{line.slice(3)}</h2>);
    } else if (line.startsWith('### ')) {
      els.push(<h3 key={`k2-${i}`} className="text-sm font-semibold text-foreground mt-3 mb-1">{line.slice(4)}</h3>);
    } else if (line.match(/^[-*]\s/)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^[-*]\s/)) {
        const text = lines[i].replace(/^[-*]\s/, '');
        items.push(<li key={`k3-${i}`} className="text-sm text-foreground/85">{renderBold(text)}</li>);
        i++;
      }
      els.push(<ul key={`ul-${i}`} className="list-disc list-inside space-y-0.5 my-1 pl-1">{items}</ul>);
      continue;
    } else if (line.trim() === '') {
      els.push(<div key={`k4-${i}`} className="h-1.5" />);
    } else {
      els.push(<p key={`k5-${i}`} className="text-sm text-foreground/85 leading-relaxed">{renderBold(line)}</p>);
    }
    i++;
  }
  return <div className="space-y-0.5">{els}</div>;
}

function renderBold(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, idx) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={`k1-${idx}`} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>
      : part
  );
}

function HealthMeter({ score, trend }) {
  const color = score >= 80 ? 'text-emerald-600' : score >= 60 ? 'text-amber-600' : score >= 40 ? 'text-orange-600' : 'text-red-600';
  const bgColor = score >= 80 ? 'bg-emerald-400' : score >= 60 ? 'bg-yellow-400' : score >= 40 ? 'bg-orange-400' : 'bg-red-400';
  const TrendIcon = trend === 'improving' ? TrendingUp : trend === 'declining' ? TrendingDown : Minus;
  const trendColor = trend === 'improving' ? 'text-emerald-600' : trend === 'declining' ? 'text-red-600' : 'text-muted-foreground';

  return (
    <div className="flex items-center gap-3" data-testid="health-meter">
      <div className="relative w-16 h-16">
        <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
          <circle cx="18" cy="18" r="15" fill="none" className="stroke-secondary" strokeWidth="3" />
          <circle cx="18" cy="18" r="15" fill="none" className={`${bgColor.replace('bg-', 'stroke-')}`}
            strokeWidth="3" strokeDasharray={`${score * 0.94} 100`} strokeLinecap="round" />
        </svg>
        <span className={`absolute inset-0 flex items-center justify-center text-lg font-bold ${color}`}>{score}</span>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Health Score</p>
        <div className={`flex items-center gap-1 ${trendColor}`}>
          <TrendIcon className="w-3.5 h-3.5" />
          <span className="text-xs capitalize">{trend}</span>
        </div>
      </div>
    </div>
  );
}

function formatValue(v) {
  if (!v) return '$0';
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
}

export default function DealView() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [dealTasks, setDealTasks] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [creatingTask, setCreatingTask] = useState(false);

  const fetchDeal = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/deals/${dealId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load deal');
      const d = await res.json();
      setData(d);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { fetchDeal(); }, [fetchDeal]);

  useEffect(() => {
    if (data?.deal?.company_name) fetchDealTasks();
  }, [data?.deal?.company_name]);

  const fetchDealTasks = async () => {
    if (!data?.deal?.company_name) return;
    try {
      const res = await fetch(`${API_URL}/api/tasks/by-deal/${encodeURIComponent(data.deal.company_name)}`, { credentials: 'include' });
      if (res.ok) setDealTasks(await res.json());
    } catch (e) { console.error(e); }
  };

  const createDealTask = async () => {
    if (!newTaskTitle.trim()) return;
    setCreatingTask(true);
    try {
      const res = await fetch(`${API_URL}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: newTaskTitle, company: data.deal.company_name, deal_id: dealId, priority: 'medium' })
      });
      if (res.ok) { toast.success('Task created'); setNewTaskTitle(''); fetchDealTasks(); }
    } catch (e) { toast.error('Failed'); }
    finally { setCreatingTask(false); }
  };

  const toggleDealTask = async (task) => {
    const nextStatus = task.status === 'done' ? 'todo' : 'done';
    try {
      await fetch(`${API_URL}/api/tasks/${task.task_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: nextStatus })
      });
      fetchDealTasks();
    } catch (e) { console.error(e); }
  };

  const generateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const res = await fetch(`${API_URL}/api/deals/${dealId}/summary`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to generate summary');
      const d = await res.json();
      setData(prev => ({ ...prev, deal: { ...prev.deal, ai_summary: d.summary } }));
      toast.success('Summary generated');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGeneratingSummary(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <p className="text-muted-foreground">Deal not found</p>
          <Button onClick={() => navigate('/pipeline')}>Back to Pipeline</Button>
        </div>
      </Layout>
    );
  }

  const { deal, calls, stakeholders, objections, timeline, next_steps } = data;
  const openObjections = objections.filter(o => o.status === 'open');
  const resolvedObjections = objections.filter(o => o.status === 'resolved');
  const winSignals = timeline.filter(t => t.type === 'win_signal');
  const lossSignals = timeline.filter(t => t.type === 'loss_signal');

  return (
    <Layout>
      <div className="space-y-6" data-testid="deal-view-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <button onClick={() => navigate('/pipeline')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2">
              <ArrowLeft className="w-4 h-4" /> Back to Pipeline
            </button>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground" data-testid="deal-name">{deal.deal_name}</h1>
              <Badge variant="outline" className="text-xs">{deal.status}</Badge>
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><Building className="w-3.5 h-3.5" />{deal.company_name}</span>
              <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{deal.call_count} calls</span>
              <span className="flex items-center gap-1"><Target className="w-3.5 h-3.5" />{deal.deal_stage}</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Estimated Value</p>
              <p className="text-xl font-bold text-emerald-600" data-testid="deal-value">{formatValue(deal.estimated_value)}</p>
            </div>
            <HealthMeter score={deal.health_score} trend={deal.health_trend} />
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-secondary/50 border border-border/50">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="calls">Calls ({calls.length})</TabsTrigger>
            <TabsTrigger value="stakeholders">Stakeholders ({stakeholders.length})</TabsTrigger>
            <TabsTrigger value="tasks">Tasks ({dealTasks.length})</TabsTrigger>
            <TabsTrigger value="objections">Objections ({objections.length})</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
            <TabsTrigger value="summary">AI Summary</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-4 space-y-4">
            <div className="grid gap-4 md:grid-cols-4">
              <Card className="bg-card border-border/50">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Health Score</p>
                  <p className="text-2xl font-bold text-foreground">{deal.health_score}</p>
                  <p className={`text-xs mt-0.5 ${deal.health_trend === 'improving' ? 'text-emerald-600' : deal.health_trend === 'declining' ? 'text-red-600' : 'text-muted-foreground'}`}>
                    {deal.health_trend}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border/50">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Estimated Value</p>
                  <p className="text-2xl font-bold text-emerald-600">{formatValue(deal.estimated_value)}</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border/50">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Open Objections</p>
                  <p className={`text-2xl font-bold ${openObjections.length > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{openObjections.length}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{resolvedObjections.length} resolved</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border/50">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Signals</p>
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-600 font-bold">{winSignals.length} win</span>
                    <span className="text-border">/</span>
                    <span className="text-red-600 font-bold">{lossSignals.length} risk</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Score progression */}
            {deal.score_history && deal.score_history.length > 1 && (
              <Card className="bg-card border-border/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Score Progression</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end gap-1 h-20">
                    {deal.score_history.map((s, idx) => (
                      <div key={`k2-${idx}`} className="flex-1 flex flex-col items-center gap-1">
                        <span className="text-[10px] text-muted-foreground">{s}</span>
                        <div
                          className={`w-full rounded-t ${s >= 80 ? 'bg-emerald-400' : s >= 60 ? 'bg-yellow-400' : s >= 40 ? 'bg-orange-400' : 'bg-red-400'}`}
                          style={{ height: `${Math.max(s * 0.7, 4)}px` }}
                        />
                        <span className="text-[9px] text-muted-foreground">Call {idx + 1}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Next Steps */}
            {next_steps.length > 0 && (
              <Card className="bg-card border-border/50" data-testid="next-steps-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Recommended Next Steps</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {next_steps.slice(0, 8).map((step, idx) => (
                      <div key={`k3-${idx}`} className="flex items-start gap-2 p-2 rounded-lg bg-secondary/30 border border-border/50">
                        <ChevronRight className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground">{step.action}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge variant="outline" className={`text-[10px] px-1 py-0 ${step.priority === 'high' ? 'border-red-500/30 text-red-600' : 'border-border/50'}`}>
                              {step.priority}
                            </Badge>
                            <span className="text-[10px] text-muted-foreground">{step.source}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Calls Tab */}
          <TabsContent value="calls" className="mt-4 space-y-3">
            {calls.map((call) => (
              <Link key={call.call_id} to={`/report/${call.call_id}`} className="block" data-testid={`call-link-${call.call_id}`}>
                <Card className="bg-card border-border/50 hover:border-primary/40 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-foreground">{call.title}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span>{new Date(call.call_date).toLocaleDateString()}</span>
                          <span>{call.deal_stage}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {call.deal_value && (
                          <span className="text-sm font-medium text-emerald-600">{formatValue(call.deal_value)}</span>
                        )}
                        <Badge className={`text-xs ${call.risk_status === 'High' ? 'bg-red-500/15 text-red-600 border-red-500/30' : call.risk_status === 'Medium' ? 'bg-yellow-500/15 text-amber-600 border-yellow-500/30' : 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30'}`}>
                          {call.risk_status}
                        </Badge>
                        <span className="text-lg font-bold text-foreground">{call.score}</span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                    {call.summary && (
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{call.summary}</p>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </TabsContent>

          {/* Stakeholders Tab */}
          <TabsContent value="stakeholders" className="mt-4 space-y-3" data-testid="stakeholders-tab">
            {stakeholders.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No stakeholders detected</p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {stakeholders.map((s, idx) => (
                  <Card key={`k4-${idx}`} className="bg-card border-border/50">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center shrink-0">
                          <UserCircle className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">{s.name}</p>
                          <p className="text-xs text-muted-foreground">{s.role}</p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${
                              s.sentiment === 'High' ? 'border-emerald-500/30 text-emerald-600' :
                              s.sentiment === 'Low' ? 'border-red-500/30 text-red-600' :
                              'border-border/50 text-muted-foreground'
                            }`}>
                              Intent: {s.sentiment}
                            </Badge>
                            <span className="text-[10px] text-muted-foreground">{s.call_count} calls</span>
                          </div>
                          {s.pain_points.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {s.pain_points.slice(0, 3).map((pp, pidx) => (
                                <span key={pidx} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary/60 text-muted-foreground">{pp}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>


          {/* Tasks Tab */}
          <TabsContent value="tasks" className="mt-4 space-y-4" data-testid="tasks-tab">
            {/* Quick add */}
            <div className="flex gap-2">
              <input
                value={newTaskTitle}
                onChange={e => setNewTaskTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && createDealTask()}
                placeholder="Add a task..."
                className="flex-1 px-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="deal-task-input"
              />
              <Button size="sm" onClick={createDealTask} disabled={creatingTask || !newTaskTitle.trim()} className="bg-primary text-primary-foreground" data-testid="deal-task-add-btn">
                {creatingTask ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              </Button>
            </div>

            {/* Task list */}
            {dealTasks.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No tasks for this deal yet</p>
            ) : (
              <div className="space-y-1.5">
                {dealTasks.map(t => {
                  const isOverdue = t.status !== 'done' && t.due_date && new Date(t.due_date) < new Date();
                  const dueStr = t.due_date ? new Date(t.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                  return (
                    <div
                      key={t.task_id}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all ${
                        t.status === 'done' ? 'bg-secondary/30 border-border/30' : 'bg-card border-border/50'
                      } ${isOverdue ? 'border-red-500/30' : ''}`}
                      data-testid={`deal-task-${t.task_id}`}
                    >
                      <button onClick={() => toggleDealTask(t)} className="shrink-0">
                        {t.status === 'done'
                          ? <CheckCircle className="w-4 h-4 text-emerald-600" />
                          : <Circle className="w-4 h-4 text-muted-foreground" />
                        }
                      </button>
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm ${t.status === 'done' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                          {t.title}
                        </span>
                        {(dueStr || t.priority === 'high') && (
                          <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                            {dueStr && (
                              <span className={`flex items-center gap-0.5 ${isOverdue ? 'text-red-600' : ''}`}>
                                <Calendar className="w-3 h-3" />{dueStr}{isOverdue && ' (overdue)'}
                              </span>
                            )}
                            {t.priority === 'high' && <span className="text-red-600 font-medium">High</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Objections Tab */}
          <TabsContent value="objections" className="mt-4 space-y-4" data-testid="objections-tab">
            {openObjections.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-red-600 mb-2 flex items-center gap-1.5">
                  <XCircle className="w-4 h-4" /> Open Objections ({openObjections.length})
                </h3>
                <div className="space-y-2">
                  {openObjections.map((obj, idx) => (
                    <Card key={`k5-${idx}`} className="bg-card border-red-500/20">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm font-medium text-foreground">{obj.type}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">Raised {obj.occurrences}x | Severity: {obj.max_severity}/10</p>
                          </div>
                          <Badge className="bg-red-500/15 text-red-600 border-red-500/30 text-xs">Open</Badge>
                        </div>
                        {obj.concerns.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {obj.concerns.slice(0, 2).map((c, cidx) => (
                              <p key={cidx} className="text-xs text-muted-foreground italic">"{c}"</p>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {resolvedObjections.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-emerald-600 mb-2 flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4" /> Resolved ({resolvedObjections.length})
                </h3>
                <div className="space-y-2">
                  {resolvedObjections.map((obj, idx) => (
                    <Card key={`k6-${idx}`} className="bg-card border-emerald-500/20">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <p className="text-sm font-medium text-foreground">{obj.type}</p>
                          <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 text-xs">Resolved</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Appeared {obj.occurrences}x | Severity: {obj.max_severity}/10
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {objections.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No objections tracked yet</p>
            )}
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline" className="mt-4" data-testid="timeline-tab">
            {timeline.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No timeline events</p>
            ) : (
              <div className="relative pl-6 space-y-4">
                <div className="absolute left-2.5 top-2 bottom-2 w-px bg-border" />
                {timeline.map((event, idx) => {
                  const icons = {
                    call: <Phone className="w-3 h-3" />,
                    win_signal: <TrendingUp className="w-3 h-3" />,
                    loss_signal: <AlertTriangle className="w-3 h-3" />,
                    commitment: <CheckCircle className="w-3 h-3" />,
                  };
                  const colors = {
                    call: 'bg-primary/15 text-primary border-primary/30',
                    win_signal: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
                    loss_signal: 'bg-red-500/15 text-red-600 border-red-500/30',
                    commitment: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
                  };

                  return (
                    <div key={`k7-${idx}`} className="relative flex gap-3">
                      <div className={`absolute -left-3.5 w-5 h-5 rounded-full border flex items-center justify-center ${colors[event.type] || 'bg-secondary text-muted-foreground border-border'}`}>
                        {icons[event.type] || <Clock className="w-3 h-3" />}
                      </div>
                      <div className="flex-1 ml-4">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground">{event.title}</p>
                          {event.score && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0">{event.score}</Badge>
                          )}
                          {event.grade && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0">{event.grade}</Badge>
                          )}
                        </div>
                        {event.detail && <p className="text-xs text-muted-foreground mt-0.5 italic">"{event.detail}"</p>}
                        <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                          {event.date ? new Date(event.date).toLocaleString() : ''}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* AI Summary Tab */}
          <TabsContent value="summary" className="mt-4" data-testid="summary-tab">
            <Card className="bg-card border-border/50">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" /> AI Deal Summary
                  </CardTitle>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={generateSummary}
                    disabled={generatingSummary || calls.length === 0}
                    data-testid="generate-summary-btn"
                  >
                    {generatingSummary ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                    {deal.ai_summary ? 'Regenerate' : 'Generate Summary'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {deal.ai_summary ? (
                  <MarkdownBlock content={deal.ai_summary} />
                ) : (
                  <div className="text-center py-8">
                    <Sparkles className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Click "Generate Summary" to get an AI-powered analysis of all calls in this deal.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
